import React, { useEffect, useState, useCallback } from "react";
import { HeartHandshake, Moon, Wind, BookOpen, Flame, Plus, Target, Trash2, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { API, axios } from "../lib/planner";
import { AppNav } from "../components/planner/AppNav";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Slider } from "../components/ui/slider";

const CATEGORIES = [
  { key: "prayer", title: "Prayer Lifestyle", subtitle: "A steady rhythm of talking with God", verse: "Pray without ceasing. — 1 Thessalonians 5:17", color: "#556B4E", soft: "#EAF0E6", icon: HeartHandshake },
  { key: "fasting", title: "Fasting", subtitle: "Seeking Him above all else", verse: "When you fast, do not look gloomy. — Matthew 6:16", color: "#9A6F4E", soft: "#F1E8DF", icon: Moon },
  { key: "meditation", title: "Meditation", subtitle: "Stillness and reflection on His truth", verse: "Be still, and know that I am God. — Psalm 46:10", color: "#5F8683", soft: "#E4EDEC", icon: Wind },
  { key: "word", title: "Studying the Word", subtitle: "Growing deep in Scripture", verse: "Your word is a lamp to my feet. — Psalm 119:105", color: "#B0824A", soft: "#F4EBDC", icon: BookOpen },
];

const Ring = ({ percent, color, count, target }) => {
  const size = 92, stroke = 8;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, percent));
  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#EAE5D9" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c - (clamped / 100) * c} style={{ transition: "stroke-dashoffset 0.7s ease-out" }} />
      </svg>
      <div className="absolute flex flex-col items-center leading-none">
        <span className="font-serif text-2xl text-ink">{count}</span>
        <span className="font-sans text-[10px] text-ink-muted">of {target}</span>
      </div>
    </div>
  );
};

export default function ActivitiesPage() {
  const [summary, setSummary] = useState(null);
  const [logCat, setLogCat] = useState(null);
  const [goalCat, setGoalCat] = useState(null);
  const [note, setNote] = useState("");
  const [duration, setDuration] = useState(15);
  const [target, setTarget] = useState(3);
  const [intention, setIntention] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/activities/summary`);
    setSummary(r.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const meta = (key) => CATEGORIES.find((c) => c.key === key);

  const openLog = (key) => { setNote(""); setDuration(15); setLogCat(key); };
  const openGoal = (key) => {
    const s = summary?.[key];
    setTarget(s?.weekly_target || 3);
    setIntention(s?.intention || "");
    setGoalCat(key);
  };

  const submitLog = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/activities/log`, { category: logCat, note, duration_minutes: duration });
      toast.success(`${meta(logCat).title} logged. Keep growing.`);
      setLogCat(null);
      load();
    } catch (e) { toast.error("Could not log activity"); }
    finally { setSaving(false); }
  };

  const submitGoal = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/activities/goal/${goalCat}`, { weekly_target: Math.max(1, target), intention });
      toast.success("Goal updated");
      setGoalCat(null);
      load();
    } catch (e) { toast.error("Could not save goal"); }
    finally { setSaving(false); }
  };

  const deleteLog = async (id) => {
    await axios.delete(`${API}/activities/log/${id}`);
    load();
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
    catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-clay-app" data-testid="activities-page">
      <header className="sticky top-0 z-30 overflow-hidden border-b border-[#E5E1D8] bg-clay-app/90 backdrop-blur-xl">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(600px circle at 92% -40%, rgba(196,164,124,0.18), transparent 60%)" }} />
        <div className="relative mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-6 py-5 lg:px-10">
          <div>
            <p className="font-sans text-xs uppercase tracking-[0.25em] text-ink-muted">Spiritual Growth</p>
            <h1 className="font-serif text-3xl font-medium tracking-tight text-ink sm:text-4xl">Disciplines</h1>
          </div>
          <AppNav />
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8 lg:px-10">
        <p className="mb-8 max-w-2xl font-serif text-2xl italic leading-snug text-ink-soft">
          Tend to the disciplines that shape a life with God. Log a little each day — He honours the faithful, steady heart.
        </p>

        {!summary ? (
          <p className="font-serif text-xl italic text-ink-soft">Gathering your growth…</p>
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {CATEGORIES.map((cat, i) => {
              const Icon = cat.icon;
              const s = summary[cat.key] || {};
              const target = s.weekly_target || 3;
              const percent = target ? Math.round(((s.week_count || 0) / target) * 100) : 0;
              const met = (s.week_count || 0) >= target;
              return (
                <div
                  key={cat.key}
                  className="animate-rise overflow-hidden rounded-2xl border border-[#E5E1D8] bg-white shadow-[0_4px_24px_-6px_rgba(44,53,41,0.06)]"
                  style={{ animationDelay: `${i * 60}ms` }}
                  data-testid={`activity-card-${cat.key}`}
                >
                  <div className="h-1.5 w-full" style={{ backgroundColor: cat.color }} />
                  <div className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <div className="rounded-xl p-2.5" style={{ backgroundColor: cat.soft }}>
                          <Icon className="h-6 w-6" style={{ color: cat.color }} />
                        </div>
                        <div>
                          <h2 className="font-serif text-2xl leading-tight text-ink">{cat.title}</h2>
                          <p className="font-sans text-sm text-ink-soft">{cat.subtitle}</p>
                        </div>
                      </div>
                      <Ring percent={percent} color={cat.color} count={s.week_count || 0} target={target} />
                    </div>

                    <p className="mt-4 border-l-2 pl-3 font-serif text-base italic text-ink-soft" style={{ borderColor: cat.color }}>
                      {cat.verse}
                    </p>

                    {s.intention && (
                      <p className="mt-3 rounded-lg bg-clay-panel px-3 py-2 font-sans text-sm text-ink-soft">
                        <span className="text-ink-muted">My intention: </span>{s.intention}
                      </p>
                    )}

                    <div className="mt-4 flex flex-wrap items-center gap-4 font-sans text-sm">
                      <span className="flex items-center gap-1.5 text-ink-soft" data-testid={`activity-streak-${cat.key}`}>
                        <Flame className="h-4 w-4" style={{ color: cat.color }} /> {s.streak || 0} day streak
                      </span>
                      <span className="text-ink-muted">·</span>
                      <span className="text-ink-soft">{s.week_minutes || 0} min this week</span>
                      {met && <span className="rounded-full px-2 py-0.5 text-xs font-medium text-white" style={{ backgroundColor: cat.color }}>Goal met 🌿</span>}
                    </div>

                    {s.recent && s.recent.length > 0 && (
                      <div className="mt-4 space-y-1.5 border-t border-[#EFEBE1] pt-4">
                        {s.recent.map((l) => (
                          <div key={l.id} className="group flex items-center justify-between gap-2 text-sm" data-testid={`recent-log-${l.id}`}>
                            <span className="min-w-0 truncate text-ink-soft">
                              <span className="text-ink-muted">{fmtDate(l.date)}</span>
                              {l.duration_minutes > 0 && <span className="text-ink-muted"> · {l.duration_minutes}m</span>}
                              {l.note && <span> — {l.note}</span>}
                            </span>
                            <button onClick={() => deleteLog(l.id)} data-testid={`delete-log-${l.id}`} className="shrink-0 text-ink-muted opacity-0 transition-opacity group-hover:opacity-100 hover:text-destructive">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="mt-5 flex items-center gap-2">
                      <button
                        onClick={() => openLog(cat.key)}
                        data-testid={`log-today-${cat.key}`}
                        className="flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium text-white transition-opacity duration-300 hover:opacity-90"
                        style={{ backgroundColor: cat.color }}
                      >
                        <Plus className="h-4 w-4" /> Log today
                      </button>
                      <button
                        onClick={() => openGoal(cat.key)}
                        data-testid={`set-goal-${cat.key}`}
                        className="flex items-center gap-1.5 rounded-full border border-[#E5E1D8] px-4 py-2 text-sm font-medium text-ink-soft transition-colors duration-300 hover:bg-clay-panel"
                      >
                        <Target className="h-4 w-4" /> Goal
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Log dialog */}
      <Dialog open={!!logCat} onOpenChange={(o) => !o && setLogCat(null)}>
        <DialogContent className="rounded-2xl border-[#E5E1D8] bg-white p-8 sm:max-w-md" data-testid="activity-log-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl font-medium text-ink">Log {logCat && meta(logCat).title}</DialogTitle>
            <DialogDescription className="sr-only">Record today's activity for this discipline</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.1em] text-ink-muted">Duration · {duration} min</Label>
              <div className="pt-3"><Slider value={[duration]} onValueChange={(v) => setDuration(v[0])} min={0} max={180} step={5} data-testid="log-duration" /></div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.1em] text-ink-muted">Reflection (optional)</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="What did God show you?" className="rounded-lg border-[#E5E1D8] bg-clay-app focus-visible:ring-[#8FA388]" data-testid="log-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setLogCat(null)} className="rounded-full">Cancel</Button>
            <Button onClick={submitLog} disabled={saving} className="rounded-full bg-brand text-white hover:bg-brand-hover" data-testid="log-save">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />} Log it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Goal dialog */}
      <Dialog open={!!goalCat} onOpenChange={(o) => !o && setGoalCat(null)}>
        <DialogContent className="rounded-2xl border-[#E5E1D8] bg-white p-8 sm:max-w-md" data-testid="activity-goal-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl font-medium text-ink">{goalCat && meta(goalCat).title} goal</DialogTitle>
            <DialogDescription className="sr-only">Set your weekly target and intention for this discipline</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <div className="space-y-2">
              <Label htmlFor="goal-target" className="text-xs uppercase tracking-[0.1em] text-ink-muted">Times per week</Label>
              <Input id="goal-target" type="number" min={1} value={target} onChange={(e) => setTarget(parseInt(e.target.value || "0", 10))} className="rounded-lg border-[#E5E1D8] bg-clay-app focus-visible:ring-[#8FA388]" data-testid="goal-target" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.1em] text-ink-muted">My intention (optional)</Label>
              <Textarea value={intention} onChange={(e) => setIntention(e.target.value)} rows={2} placeholder="Why this matters to me…" className="rounded-lg border-[#E5E1D8] bg-clay-app focus-visible:ring-[#8FA388]" data-testid="goal-intention" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setGoalCat(null)} className="rounded-full">Cancel</Button>
            <Button onClick={submitGoal} disabled={saving} className="rounded-full bg-brand text-white hover:bg-brand-hover" data-testid="goal-save">Save goal</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
